import React, { useEffect, useRef } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const faqs = [
  {
    question: 'Do I need to book in advance?',
    answer:
      'Walk-ins are welcome if we have availability, but we recommend booking ahead to secure your preferred time and stylist — weekends fill up fast.',
  },
  {
    question: 'How long does an appointment take?',
    answer:
      'A standard cut runs 45–60 minutes. Color services can range from 1.5 to 3 hours depending on the technique. We confirm the exact window at booking.',
  },
  {
    question: 'What products do you use?',
    answer:
      'We use premium salon-grade brands including Olaplex, Kerastase, and Redken. Sensitive scalp or allergies? Let us know and we will switch to hypoallergenic options.',
  },
  {
    question: 'Do you work with curly or textured hair?',
    answer:
      'Absolutely. Several of our stylists specialize in curly, coily, and textured hair, using cutting and care techniques built for your specific pattern.',
  },
  {
    question: 'How much does it cost?',
    answer:
      'Pricing varies by service and stylist seniority. Browse our services page for a full price list, or get a personalized quote during your consultation.',
  },
  {
    question: 'What is your cancellation policy?',
    answer:
      'We ask for at least 24 hours notice to cancel or reschedule. Late cancellations may be subject to a fee so we can fairly compensate your stylist.',
  },
];

const avatars = [
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=200',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=200',
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200',
];

const FAQ = () => {
  const sectionRef = useRef(null);
  const gridRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(sectionRef.current.querySelectorAll('.faq-heading'), {
        y: 30,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.1,
        scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' },
      });

      gsap.from(gridRef.current.querySelectorAll('.faq-item'), {
        y: 30,
        opacity: 0,
        duration: 0.6,
        ease: 'power3.out',
        stagger: 0.08,
        scrollTrigger: { trigger: gridRef.current, start: 'top 80%' },
      });

      gsap.from(ctaRef.current, {
        y: 40,
        opacity: 0,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: { trigger: ctaRef.current, start: 'top 85%' },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-white py-20 sm:py-24 lg:py-28 px-4 sm:px-6 lg:px-8"
    >
      {/* Soft brand-tinted corner glows */}
      <div className="absolute -top-32 -left-32 w-[26rem] h-[26rem] rounded-full bg-[#F05A35]/15 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#FFB236]/20 blur-[130px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-14">
          <h2 className="faq-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-3">
            Frequently Asked Questions
          </h2>
          <p className="faq-heading text-base text-gray-500">
            Here are the most asked questions based from our users
          </p>
        </div>

        {/* Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
          {faqs.map((faq) => (
            <div key={faq.question} className="faq-item">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#F05A35]/15 text-[#F05A35] mb-4">
                <QuestionMarkCircleIcon className="w-5 h-5" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-2">{faq.question}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA card */}
        <div
          ref={ctaRef}
          className="mt-16 bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.06)] border border-gray-100 p-8 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left"
        >
          <div className="flex -space-x-3 shrink-0">
            {avatars.map((src, i) => (
              <div
                key={i}
                className="w-12 h-12 rounded-full ring-4 ring-white overflow-hidden shadow"
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Join Our Support Live Chat</h3>
            <p className="text-sm text-gray-500">
              If you still have other questions, why not join our live chat channel?
            </p>
          </div>

          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="shrink-0 inline-flex items-center justify-center bg-[#F05A35] hover:bg-[#d94a28] text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors"
          >
            Get in touch
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
